import i18n from "i18next";
import checkAuth from "./auth";
const API_URL = `${process.env.REACT_APP_API_URL}/en/gb/makethecut/`;
const API_SUBSCRIPTION = process.env.REACT_APP_API_SUBSCRIPTION;

export default class MacallanAPI {
  constructor(accessToken) {
    this.accessToken = accessToken;
  }

  async apiCall(
    apiMethod = "",
    data = null,
    httpMethod = "GET",
    returnValue = undefined
  ) {

    const urlParams = new URLSearchParams(window.location.search);
    const locale = urlParams.get('lng');
    const deliveryLocation = urlParams.get('delivery_location');

    const fetchProps = {
      method: httpMethod, // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      credentials: "same-origin", // include, *same-origin, omit
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.accessToken}`,
        "Ocp-Apim-Subscription-Key": API_SUBSCRIPTION,
      },
      redirect: "follow", // manual, *follow, error
      referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
    };

    if (data) {
      fetchProps.body = JSON.stringify(data);
    }

    // combine the locale and delivery location to be used in the api request
    let urlLang = `${locale || 'en'}/${deliveryLocation || 'gb'}`
    const url = API_URL.replace("en/gb", urlLang);

    let response = await fetch(`${url}${apiMethod}`, fetchProps);

    if (!response.ok) {
      // if 401 returned - try to authenticate again and run the query once more
      if (response.status === 401) {
        let accessToken = false;
        try {
          accessToken = await checkAuth();
        } catch (err) {
          console.log(err);
        }
        if (accessToken) {
          this.setToken(accessToken);
          fetchProps.headers.Authorization = `Bearer ${accessToken}`;
          response = await fetch(`${url}${apiMethod}`, fetchProps);
          if (!response.ok) {
            throw new Error(`An error has occured: ${response.status}`);
          }
        } else {
          window.location.href = process.env.REACT_APP_MACALLAN_REDIRECT;
        }
      } else {
        throw new Error(`An error has occured: ${response.status}`);
      }
    }
    //.then(res => res.json())
    //.catch((err) => console.log(err));
    if (returnValue !== undefined) {
      return returnValue;
    } else {
      const responseData = response.status === 204 ? "" : response;
      return responseData;
    }
  }

  setToken(accessToken) {
    this.accessToken = accessToken;
  }

  getProfileAndAnswers() {
    return this.apiCall()
      .then((res) => res.json())
      .catch((e) => {
        console.log(e);
      });
  }

  // need to know first the structure to be sent for question and answers
  async setAnswer(page, progress = {}, markedForLater = []) {
    /*
    progress = {
      overallPercentageComplete: 80,
      chapterOnePercentageComplete: 100,
      chapterTwoPercentageComplete: 85,
      chapterThreePercentageComplete: 95,
      chapterFourPercentageComplete: 75.2,
    };
    */

    if (!page.crmId) return;
    let answers = "";
    const noneElements = [
      "no_other_city",
      "no_experiences",
      "no_macallan_own",
      "no_macallan_like_to_own",
      "no_experiences",
    ];
    page.questions.forEach(function (q) {
      // if slider question is empty - set the value to "3"
      if (q.customWidget?.name === "nouislider") {
        answers = q.value === undefined ? 3 : parseInt(q.value);
        // 'None' options sent as null
      } else if (q.name === "no_macallan_bought") {
        if (q.value) {
          answers = ["no"];
        }
      } else if (q.name === "whisky_macallan_only") {
        if (q.value) {
          answers = ["not_applicable"];
        }
      } else if (noneElements.indexOf(q.name) !== -1) {
        if (q.value) {
          answers = null;
        }
      } else if (q.selectedItem) {
        // single selection
        if (!q.crmId) return;
        answers += q.selectedItem.text;
        const valueIndex = q.visibleChoices.findIndex(
          (item) => item.value === q.value
        );
        answers = q.crmId[valueIndex];
        if (q.comment) answers += `|${q.comment}`;
      } else if (q.selectedItems) {
        if (!q.crmId) return;
        answers = [];
        q.selectedItems.forEach(function (answer) {
          if (!answer) return;
          const valueIndex = q.visibleChoices.findIndex(
            (item) => item.value === answer.value
          );
          let single = q.crmId[valueIndex];
          if (q.comment && single === "other") single += `|${q.comment}`;
          answers.push(single);
        });
      } else if (q.value !== null && q.value !== undefined) {
        // free text
        answers += q.value;
      }

      //if (q.comment) answers += ` ${q.comment}`;
    });
    const data = {
      ...progress,
      answers: {
        [page.crmId]: answers,
      },
      markedForLater,
    };

    let that = this
    // Get a recaptcha token from Google then continue with ballot entry.
    await window.grecaptcha.ready(function() {
      window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_V3_KEY, {action: 'submit'}).then((token) => {
        data.token = token;
        return that.apiCall("", data, "PATCH", answers);
      })
      .catch((e) => {
        console.log(e);
      });
    });
  }

  // only send markedForLater, without answers
  async setMarkedForLater(progress = {}, markedForLater = []) {
    const data = {
      answers: {},
      ...progress,
      markedForLater,
    };

    let that = this
    // Get a recaptcha token from Google then continue with the submission.
    await window.grecaptcha.ready(function() {
      window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_V3_KEY, {action: 'submit'}).then((token) => {
        data.token = token;
        return that.apiCall("", data, "PATCH");
      })
      .catch((e) => {
        console.log(e);
      });
    });
  }

  // base64 encoded visualisation image - URL of saved image returned
  setVizImage(encodedImage) {
    return this.apiCall("", encodedImage, "POST").then((res) => res.text());
  }

  getMacallanWhiskies() {
    return this.apiCall("products")
      .then((res) => res.json())
      .catch((e) => {
        console.log(e);
      });
  }

  getConfig() {
    return this.apiCall("config")
      .then((res) => res.json())
      .catch((e) => {
        console.log(e);
      });
  }

  setConfig(configData) {
    const config = {
      config: JSON.stringify(configData),
    };

    this.apiCall("config", config, "POST").catch((e) => {
      console.log(e);
    });
  }
}
